import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

/**
 * Drag-to-resize state for a side panel, persisted to localStorage.
 *
 * `side` is the edge of the *panel* the handle sits on: a left-hand column
 * (nav, cases list) has its handle on its `right` edge and grows as the
 * pointer moves right; a right-hand sidebar (tools, chat) has its handle on
 * its `left` edge and grows as the pointer moves left.
 *
 * Width is clamped to [min, max] and additionally to `maxViewportFraction`
 * of the window so a panel saved on a wide monitor can't swallow a laptop.
 * Double-clicking the handle resets to `defaultWidth`.
 */
export interface ResizablePanelOptions {
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth?: number;
  side: "left" | "right";
  maxViewportFraction?: number;
}

const readStored = (key: string): number | null => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

export function useResizablePanel({
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth = 900,
  side,
  maxViewportFraction = 0.6,
}: ResizablePanelOptions) {
  const clamp = useCallback(
    (w: number) => {
      const viewportCap =
        typeof window !== "undefined"
          ? Math.max(minWidth, window.innerWidth * maxViewportFraction)
          : maxWidth;
      return Math.round(Math.min(maxWidth, viewportCap, Math.max(minWidth, w)));
    },
    [minWidth, maxWidth, maxViewportFraction],
  );

  const [width, setWidth] = useState<number>(() => {
    const stored = readStored(storageKey);
    return clamp(stored ?? defaultWidth);
  });
  const [isResizing, setIsResizing] = useState(false);

  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const latestRef = useRef(width);
  latestRef.current = width;

  const persist = useCallback(
    (w: number) => {
      try {
        localStorage.setItem(storageKey, String(w));
      } catch {
        // Storage unavailable: keep in-memory only
      }
    },
    [storageKey],
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startWidth: latestRef.current };
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      setIsResizing(true);
    },
    [],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const next = side === "right" ? drag.startWidth + dx : drag.startWidth - dx;
      setWidth(clamp(next));
    },
    [side, clamp],
  );

  const endDrag = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!dragRef.current) return;
      dragRef.current = null;
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
      setIsResizing(false);
      persist(latestRef.current);
    },
    [persist],
  );

  const reset = useCallback(() => {
    const w = clamp(defaultWidth);
    setWidth(w);
    persist(w);
  }, [clamp, defaultWidth, persist]);

  // Global cursor + no text selection while dragging, even when the pointer
  // crosses iframes/canvases that would otherwise swallow the events.
  useEffect(() => {
    if (!isResizing) return;
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [isResizing]);

  const handleProps = {
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onDoubleClick: reset,
  };

  return { width, isResizing, handleProps, reset, setWidth };
}
